import { useEffect, useRef } from 'react';
import type { ChapterStatus } from '@gbcmc/survey-core';

interface ChapterNavProps {
  open: boolean;
  onClose: () => void;
  statuses: ChapterStatus[];
  currentChapterId: string;
  onJump: (chapterId: string) => void;
}

// Panel lateral de capítulos: permite volver a capítulos anteriores/completados
// para revisar o cambiar respuestas, y muestra el avance. Modal accesible:
// role="dialog", cierre con Esc/backdrop, foco inicial en el botón cerrar.
export function ChapterNav({ open, onClose, statuses, currentChapterId, onJump }: ChapterNavProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const completed = statuses.filter((s) => s.complete).length;

  // Un capítulo es navegable si es el actual o si todos los anteriores están
  // completos (no se puede saltar adelante sin completar lo previo).
  const isNavigable = (i: number) =>
    statuses[i]!.chapter.id === currentChapterId || statuses.slice(0, i).every((s) => s.complete);

  return (
    <>
      <div className="gb-chapnav__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="gb-chapnav"
        role="dialog"
        aria-modal="true"
        aria-label="Capítulos de la evaluación"
      >
        <div className="gb-chapnav__head">
          <h2 className="gb-chapnav__title">Capítulos</h2>
          <p className="gb-chapnav__progress">
            Has completado {completed} de {statuses.length} capítulos
          </p>
          <button
            ref={closeRef}
            type="button"
            className="gb-chapnav__close"
            onClick={onClose}
            aria-label="Cerrar capítulos"
          >
            ×
          </button>
        </div>
        <ul className="gb-chapnav__list">
          {statuses.map((s, i) => {
            const isCurrent = s.chapter.id === currentChapterId;
            const nav = isNavigable(i);
            const state = s.complete
              ? 'Completo'
              : isCurrent
                ? 'En curso'
                : nav
                  ? 'Disponible'
                  : 'Pendiente';
            return (
              <li key={s.chapter.id}>
                <button
                  type="button"
                  className={
                    'gb-chapnav__item' +
                    (s.complete ? ' is-complete' : '') +
                    (isCurrent ? ' is-current' : '')
                  }
                  disabled={!nav}
                  aria-current={isCurrent ? 'step' : undefined}
                  onClick={() => onJump(s.chapter.id)}
                >
                  <span className="gb-chapnav__num" aria-hidden="true">
                    {s.complete ? '✓' : s.chapter.num}
                  </span>
                  <span className="gb-chapnav__body">
                    <span className="gb-chapnav__label">{s.chapter.label}</span>
                    <span className="gb-chapnav__meta">
                      {s.answered}/{s.total} respondidas · {state}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
